import {
    MEDIAPIPE_WASM,
    MEDIAPIPE_VISION,
    MEDIAPIPE_MODEL,
    FACEAPI_CDN,
    FACEAPI_MODEL_CDN,
    AGE_INPUT_SIZE,
    STABLE_FRAMES_REQUIRED,
    POSITION_CHECK_MS,
    MIN_BRIGHTNESS,
    TASK_TIMEOUT_MS,
    MIN_TASK_TIME_MS,
    TASK_COUNT,
    REQUIRED_TASK_PASSES,
    MIN_AGE_SAMPLES,
    MIN_FACE_SCORE,
    PASS_MARGIN,
    BORDERLINE_BAND,
    MAX_AGE_SPREAD,
} from './constants.js';

function loadScript(url) {
    return new Promise((resolve, reject) => {
        if (typeof window === 'undefined') return reject(new Error('No DOM available'));

        const existing = document.querySelector(`script[src="${url}"]`);
        if (existing) {
            if (window.faceapi) return resolve(window.faceapi);
            existing.addEventListener('load', () => resolve(window.faceapi));
            existing.addEventListener('error', () => reject(new Error(`Failed to load ${url}`)));
            return;
        }

        const script = document.createElement('script');
        script.src = url;
        script.crossOrigin = 'anonymous';
        script.onload = () => resolve(window.faceapi);
        script.onerror = () => reject(new Error(`Failed to load ${url}`));
        document.head.appendChild(script);
    });
}

/* ── Face tracker (MediaPipe) ──────────────────── */

let FaceLandmarker = null;
let landmarker = null;
let lastTimestampMs = -1;
let visionModule = null;

export async function loadVision() {
    if (visionModule) return visionModule;
    visionModule = await import(MEDIAPIPE_VISION);
    FaceLandmarker = visionModule.FaceLandmarker;
    return visionModule;
}

export async function loadModel() {
    const response = await fetch(MEDIAPIPE_MODEL);
    if (!response.ok) throw new Error('Failed to load face landmarker model');
    return new Uint8Array(await response.arrayBuffer());
}

export async function initTracker(modelBuffer) {
    const vision = await loadVision();
    const resolver = await vision.FilesetResolver.forVisionTasks(MEDIAPIPE_WASM);

    if (landmarker) landmarker.close();
    lastTimestampMs = -1;

    landmarker = await FaceLandmarker.createFromOptions(resolver, {
        baseOptions: { modelAssetBuffer: new Uint8Array(modelBuffer), delegate: 'GPU' },
        runningMode: 'VIDEO',
        numFaces: 2,
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true,
    });
}

export function track(video, timestampMs) {
    if (!landmarker) return null;

    const normalized = normalizeTimestamp(timestampMs);
    const result = landmarker.detectForVideo(video, normalized);
    const faceCount = result.faceLandmarks?.length ?? 0;

    if (faceCount === 0) return { faceCount: 0, timestampMs: normalized };

    const landmarks = result.faceLandmarks[0];

    return {
        faceCount,
        timestampMs: normalized,
        landmarks,
        blendshapes: parseBlendshapes(result.faceBlendshapes?.[0]),
        headPose: extractHeadPose(result.facialTransformationMatrixes?.[0]),
        boundingBox: computeBoundingBox(landmarks),
    };
}

export function destroyTracker() {
    if (landmarker) {
        landmarker.close();
        landmarker = null;
    }
    lastTimestampMs = -1;
}

export function isTrackerReady() {
    return landmarker !== null;
}

function normalizeTimestamp(timestampMs) {
    const safe = Number.isFinite(timestampMs) ? timestampMs : performance.now();
    const normalized = Math.max(Math.floor(safe), lastTimestampMs + 1);
    lastTimestampMs = normalized;
    return normalized;
}

function parseBlendshapes(blendshapeResult) {
    if (!blendshapeResult?.categories) return {};
    const map = {};
    for (const category of blendshapeResult.categories) {
        map[category.categoryName] = category.score;
    }
    return map;
}

function extractHeadPose(matrix) {
    if (!matrix?.data || matrix.data.length < 16) return { yaw: 0, pitch: 0, roll: 0 };
    const m = matrix.data;
    const deg = 180 / Math.PI;
    return {
        yaw: Math.atan2(m[8], m[10]) * deg,
        pitch: Math.asin(-Math.max(-1, Math.min(1, m[9]))) * deg,
        roll: Math.atan2(m[1], m[5]) * deg,
    };
}

function computeBoundingBox(landmarks) {
    let minX = 1;
    let minY = 1;
    let maxX = 0;
    let maxY = 0;

    for (const point of landmarks) {
        if (point.x < minX) minX = point.x;
        if (point.y < minY) minY = point.y;
        if (point.x > maxX) maxX = point.x;
        if (point.y > maxY) maxY = point.y;
    }

    const width = maxX - minX;
    const height = maxY - minY;
    return { x: minX, y: minY, width, height, area: width * height };
}

/* ── Positioning ───────────────────────────────── */

function sampleBrightness(video) {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (!ctx) return Infinity;

    ctx.drawImage(video, 0, 0, 64, 64);
    const pixels = ctx.getImageData(0, 0, 64, 64).data;
    let sum = 0;
    for (let i = 0; i < pixels.length; i += 4) {
        sum += (pixels[i] * 299 + pixels[i + 1] * 587 + pixels[i + 2] * 114) / 1000;
    }
    return sum / (pixels.length / 4);
}

export function startPositioning(video, callbacks, tracker = track) {
    let stableFrames = 0;
    let cancelled = false;

    const check = () => {
        if (cancelled) return;

        if (sampleBrightness(video) < MIN_BRIGHTNESS) {
            callbacks.onStatus?.('More light needed');
            stableFrames = 0;
            setTimeout(check, POSITION_CHECK_MS);
            return;
        }

        const result = tracker(video, performance.now());

        if (!result || result.faceCount === 0) {
            callbacks.onStatus?.('Look at the camera');
            stableFrames = 0;
        } else if (result.faceCount > 1) {
            callbacks.onStatus?.('Only one person please');
            stableFrames = 0;
        } else {
            callbacks.onStatus?.('Hold still…');
            stableFrames++;
        }

        if (stableFrames >= STABLE_FRAMES_REQUIRED) {
            callbacks.onReady?.();
            return;
        }

        setTimeout(check, POSITION_CHECK_MS);
    };

    check();
    return {
        cancel: () => {
            cancelled = true;
        },
    };
}

/* ── Liveness (head-movement tasks) ────────────── */

const TASKS = [
    { id: 'turn-left', instruction: 'Turn your head to the left', check: (h) => yawShift(h, 20) },
    {
        id: 'turn-right',
        instruction: 'Turn your head to the right',
        check: (h) => yawShift(h, -20),
    },
    { id: 'nod', instruction: 'Nod your head down then up', check: (h) => nod(h) },
    { id: 'blink-twice', instruction: 'Blink twice', check: (h) => doubleBlink(h) },
    { id: 'move-closer', instruction: 'Move closer then back', check: (h) => distanceChange(h) },
];

export function pickTasks(count = TASK_COUNT) {
    return [...TASKS].sort(() => Math.random() - 0.5).slice(0, count);
}

export function createSession(tasks) {
    return {
        tasks: tasks || pickTasks(),
        currentIndex: 0,
        history: [],
        taskStartTime: 0,
        completedTasks: 0,
        requiredPasses: REQUIRED_TASK_PASSES,
        failed: false,
        failReason: null,
    };
}

export function processFrame(session, tracking) {
    if (session.failed || isComplete(session)) return;
    if (!tracking || tracking.faceCount === 0) return;

    if (tracking.faceCount > 1) {
        session.failed = true;
        return;
    }

    if (session.history.length === 0) session.taskStartTime = tracking.timestampMs;

    session.history.push({
        timestamp: tracking.timestampMs,
        headPose: tracking.headPose,
        blendshapes: tracking.blendshapes,
        boundingBox: tracking.boundingBox,
    });

    const elapsed = tracking.timestampMs - session.taskStartTime;

    if (elapsed > TASK_TIMEOUT_MS) return advanceTask(session);
    if (elapsed < MIN_TASK_TIME_MS) return;

    const task = session.tasks[session.currentIndex];
    if (!task.check(session.history)) return;

    if (isSuspicious(session.history)) {
        session.failed = true;
        return;
    }

    session.completedTasks++;
    advanceTask(session);
}

export function isComplete(session) {
    return session.currentIndex >= session.tasks.length;
}

export function isPassed(session) {
    return session.completedTasks >= session.requiredPasses;
}

export function currentInstruction(session) {
    if (isComplete(session)) return null;
    return session.tasks[session.currentIndex].instruction;
}

export function currentTaskId(session) {
    if (isComplete(session)) return null;
    return session.tasks[session.currentIndex].id;
}

export function progress(session) {
    if (session.tasks.length === 0) return 1;
    return Math.min(session.currentIndex / session.tasks.length, 1);
}

function advanceTask(session) {
    session.currentIndex++;
    session.history = [];
    session.taskStartTime = 0;

    const remaining = session.tasks.length - session.currentIndex;
    if (session.completedTasks + remaining < session.requiredPasses) {
        session.failed = true;
    }
}

function yawShift(history, targetDelta) {
    if (history.length < 5) return false;
    const baseYaw = history[0].headPose.yaw;
    const direction = Math.sign(targetDelta);
    const threshold = Math.abs(targetDelta);
    return history.some((entry) => (entry.headPose.yaw - baseYaw) * direction > threshold);
}

function nod(history) {
    if (history.length < 10) return false;
    const basePitch = history[0].headPose.pitch;
    let wentDown = false;
    let cameBack = false;
    for (const entry of history) {
        const delta = entry.headPose.pitch - basePitch;
        if (delta > 15) wentDown = true;
        if (wentDown && Math.abs(delta) < 8) cameBack = true;
    }
    return wentDown && cameBack;
}

function doubleBlink(history) {
    if (history.length < 10) return false;
    let blinkCount = 0;
    let eyesClosed = false;
    for (const entry of history) {
        const closed =
            (entry.blendshapes.eyeBlinkLeft ?? 0) > 0.6 &&
            (entry.blendshapes.eyeBlinkRight ?? 0) > 0.6;
        if (closed && !eyesClosed) {
            blinkCount++;
            eyesClosed = true;
        } else if (!closed) {
            eyesClosed = false;
        }
    }
    return blinkCount >= 2;
}

function distanceChange(history) {
    if (history.length < 10) return false;
    const baseArea = history[0].boundingBox.area;
    let wentCloser = false;
    let cameBack = false;
    for (const entry of history) {
        const ratio = entry.boundingBox.area / baseArea;
        if (ratio > 1.3) wentCloser = true;
        if (wentCloser && ratio < 1.15) cameBack = true;
    }
    return wentCloser && cameBack;
}

export function isSuspicious(history) {
    if (history.length < 5) return false;
    for (let i = 1; i < history.length; i++) {
        const dy = Math.abs(history[i].headPose.yaw - history[i - 1].headPose.yaw);
        const dp = Math.abs(history[i].headPose.pitch - history[i - 1].headPose.pitch);
        if (dy + dp >= 0.1) return false;
    }
    return true;
}

/* ── Age estimation (face-api) ─────────────────── */

let ageReady = false;
let faceapi = null;

export async function initAgeEstimator() {
    if (ageReady) return;

    faceapi = await loadScript(FACEAPI_CDN);
    await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(FACEAPI_MODEL_CDN),
        faceapi.nets.faceLandmark68TinyNet.loadFromUri(FACEAPI_MODEL_CDN),
        faceapi.nets.ageGenderNet.loadFromUri(FACEAPI_MODEL_CDN),
    ]);
    ageReady = true;
}

export async function estimateAge(canvas) {
    if (!faceapi) throw new Error('Age estimator not initialized');

    const options = new faceapi.TinyFaceDetectorOptions({ inputSize: AGE_INPUT_SIZE });
    const detection = await faceapi
        .detectSingleFace(canvas, options)
        .withFaceLandmarks(true)
        .withAgeAndGender();

    if (!detection) return null;
    return { age: detection.age, gender: detection.gender, score: detection.detection.score };
}

export async function estimateAgeBurst(frames) {
    const results = [];
    for (const frame of frames) {
        const result = await estimateAge(frame);
        if (result) results.push(result);
    }
    return results;
}

export function isInitialized() {
    return ageReady;
}

/* ── Age decision policy ───────────────────────── */

function median(values) {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function correctBias(age) {
    if (age >= 25) return age;
    return Math.max(0, age - Math.min((25 - age) * 0.35, 4));
}

export function decide(samples, minAge = 18) {
    const usable = (samples || []).filter(
        (sample) => sample && (sample.score == null || sample.score >= MIN_FACE_SCORE)
    );

    if (usable.length < MIN_AGE_SAMPLES) {
        return { outcome: 'retry', estimatedAge: null, reason: 'insufficient' };
    }

    const ages = usable.map((sample) => correctBias(sample.age));
    const center = median(ages);
    const spread = median(ages.map((age) => Math.abs(age - center)));

    if (spread > MAX_AGE_SPREAD) {
        return { outcome: 'retry', estimatedAge: center, reason: 'unstable' };
    }

    if (center >= minAge + PASS_MARGIN) {
        return { outcome: 'pass', estimatedAge: center, reason: 'clear' };
    }

    if (center >= minAge - BORDERLINE_BAND) {
        return { outcome: 'retry', estimatedAge: center, reason: 'borderline' };
    }

    return { outcome: 'fail', estimatedAge: center, reason: 'underage' };
}
